import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/AppShell";
import { LocationSelect, CategorySelect } from "@/components/Selects";
import { UpgradeHint } from "@/components/UpgradeHint";
import { api, fileUrl, resizeImage } from "@/lib/api";
import { useCategories, useCatLabel, useErrorToast } from "@/lib/hooks";
import { useAuth, useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const EMPTY = { name: "", category_id: null, description: "", notes: "", quantity: 1, min_quantity: "", location_id: null, photo_path: null };

const PhotoBox = ({ form, busy, onFile, onAi, aiAllowed }) => {
  const { t } = useI18n();
  const ref = useRef();
  return (
    <div className="space-y-3">
      <button type="button" data-testid="item-photo-button" onClick={() => ref.current.click()}
        className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 transition-colors">
        {form.photo_path ? <img src={fileUrl(form.photo_path)} alt="" className="h-full w-full object-cover" data-testid="item-photo-preview" />
          : <span className="flex flex-col items-center gap-2 text-slate-500"><Camera className="w-10 h-10" /><span className="text-sm font-medium px-4 text-center">{t("item.takePhoto")}</span></span>}
        {busy && <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900/60 text-white text-sm"><Loader2 className="w-6 h-6 animate-spin" />{busy}</span>}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid="item-photo-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
      {form.photo_path && (aiAllowed
        ? <Button type="button" data-testid="item-ai-button" variant="outline" onClick={onAi} disabled={!!busy} className="w-full h-12 gap-2 border-blue-200 text-blue-700"><Sparkles className="w-4 h-4" />{t("item.aiAnalyze")}</Button>
        : <UpgradeHint text={t("item.aiLocked")} testId="ai-locked" />)}
    </div>
  );
};

export default function ItemForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { features } = useWs();
  const showErr = useErrorToast();
  const { data: cats = [] } = useCategories();
  const catLabel = useCatLabel();
  const [form, setForm] = useState({ ...EMPTY, location_id: params.get("location") });
  const [busy, setBusy] = useState("");
  const { data: existing } = useQuery({ queryKey: ["item", id], queryFn: () => api.get(`/items/${id}`).then((r) => r.data), enabled: !!id });
  useEffect(() => { if (existing) setForm({ ...EMPTY, ...existing, min_quantity: existing.min_quantity ?? "" }); }, [existing]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const runAi = async (path) => {
    setBusy(t("item.aiAnalyzing"));
    try {
      const { data } = await api.post("/ai/analyze", { photo_path: path });
      setForm((f) => ({ ...f, name: data.name || f.name, description: data.description || f.description,
        category_id: data.category_id || f.category_id, quantity: data.quantity || f.quantity }));
      toast.success(t("item.aiDone"));
      refresh();
    } catch (e) { showErr(e); } finally { setBusy(""); }
  };

  const onFile = async (file) => {
    setBusy(t("item.uploading"));
    try {
      const blob = await resizeImage(file);
      const fd = new FormData();
      fd.append("file", blob, "photo.jpg");
      const { data } = await api.post("/upload", fd);
      set("photo_path")(data.path);
      setBusy("");
      if (features.ai && !id) await runAi(data.path);
    } catch (e) { showErr(e); setBusy(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, quantity: Number(form.quantity) || 0, min_quantity: form.min_quantity === "" ? null : Number(form.min_quantity) };
      const { data } = id ? await api.put(`/items/${id}`, body) : await api.post("/items", body);
      ["items", "locations", "location", "item", "movements"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      refresh();
      toast.success(id ? t("common.saved") : t("item.created"));
      navigate(`/items/${id || data.id}`, { replace: true });
    } catch (err) { showErr(err); }
  };

  return (
    <div>
      <PageHeader title={id ? t("item.edit") : t("item.add")} testId="item-form-title" />
      <form onSubmit={save} className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-8 fade-up">
        <PhotoBox form={form} busy={busy} onFile={onFile} onAi={() => runAi(form.photo_path)} aiAllowed={features.ai} />
        <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
          <div className="space-y-1.5"><Label>{t("common.name")}</Label>
            <Input data-testid="item-name-input" className="h-12 text-base" value={form.name} onChange={(e) => set("name")(e.target.value)} required /></div>
          <div className="space-y-1.5"><Label>{t("common.location")}</Label><LocationSelect value={form.location_id} onChange={set("location_id")} testId="item-location-select" /></div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div className="space-y-1.5"><Label>{t("common.category")}</Label><CategorySelect value={form.category_id} onChange={set("category_id")} cats={cats} label={catLabel} testId="item-category-select" /></div>
            <div className="space-y-1.5"><Label>{t("common.quantity")}</Label>
              <Input data-testid="item-quantity-input" type="number" min={0} className="h-12 font-mono" value={form.quantity} onChange={(e) => set("quantity")(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>{t("stock.min")}</Label>
            <Input data-testid="item-min-quantity-input" type="number" min={0} className="h-12 font-mono w-40" value={form.min_quantity} onChange={(e) => set("min_quantity")(e.target.value)} />
            <p className="text-xs text-slate-500">{t("stock.minHint")}</p></div>
          <div className="space-y-1.5"><Label>{t("common.description")}</Label>
            <Textarea data-testid="item-description-input" rows={3} value={form.description} onChange={(e) => set("description")(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>{t("common.notes")}</Label>
            <Textarea data-testid="item-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} /></div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="item-save-button" type="submit" disabled={!!busy} className="h-12 flex-1 sm:flex-none sm:px-8 text-base">{t("common.save")}</Button>
            <Button type="button" variant="ghost" className="h-12" onClick={() => navigate(-1)} data-testid="item-cancel-button">{t("common.cancel")}</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
